import React, { useState, useEffect, useCallback, useRef } from 'react';
import { SpriteFourStep, SpriteFrame } from './Experimental';
import redSprite from '../images/bulba/characters/Red.png';
import track04 from '../images/bulba/games/04NewBarkTown.mp3';
import track33 from '../images/bulba/games/33AzaleaTown.mp3';
import grassFlat        from '../images/bulba/textures/grass2.png';
import ogCenter         from '../images/bulba/textures/grass2.png';
import ogTop            from '../images/bulba/textures/grassTop.png';
import ogBottom         from '../images/bulba/textures/grassBottom.png';
import ogLeft           from '../images/bulba/textures/grassLeft.png';
import ogRight          from '../images/bulba/textures/grassRight.png';
import ogTopLeft        from '../images/bulba/textures/grassTopLeft.png';
import ogTopRight       from '../images/bulba/textures/grassTopRight.png';
import ogBottomLeft     from '../images/bulba/textures/grassBottomLeft.png';
import ogBottomRight    from '../images/bulba/textures/grassBottomRight.png';
import lgTopLeft     from '../images/bulba/textures/LightGrass/sprite-597-1.png';
import lgTop         from '../images/bulba/textures/LightGrass/sprite-597-2.png';
import lgTopRight    from '../images/bulba/textures/LightGrass/sprite-597-3.png';
import lgLeft        from '../images/bulba/textures/LightGrass/sprite-598-1.png';
import lgCenter      from '../images/bulba/textures/LightGrass/sprite-598-2.png';
import lgRight       from '../images/bulba/textures/LightGrass/sprite-598-3.png';
import lgBottomLeft  from '../images/bulba/textures/LightGrass/sprite-599-1.png';
import lgBottom      from '../images/bulba/textures/LightGrass/sprite-599-2.png';
import lgBottomRight from '../images/bulba/textures/LightGrass/sprite-599-3.png';

import dgTopLeft     from '../images/bulba/textures/DarkGrass/sprite-607-4.png';
import dgTop         from '../images/bulba/textures/DarkGrass/sprite-607-5.png';
import dgTopRight    from '../images/bulba/textures/DarkGrass/sprite-607-6.png';
import dgLeft        from '../images/bulba/textures/DarkGrass/sprite-608-4.png';
import dgCenter      from '../images/bulba/textures/DarkGrass/sprite-608-5.png';
import dgRight       from '../images/bulba/textures/DarkGrass/sprite-608-6.png';
import dgBottomLeft  from '../images/bulba/textures/DarkGrass/sprite-609-4.png';
import dgBottom      from '../images/bulba/textures/DarkGrass/sprite-609-5.png';
import dgBottomRight from '../images/bulba/textures/DarkGrass/sprite-609-6.png';

const TRACKS = [
  { src: track04, label: 'New Bark Town' },
  { src: track33, label: 'Azalea Town' },
];

const ROWS_DESKTOP = 8;
const ROWS_MOBILE  = 5;
const PICKER_MULT = 1;

const DIR_TO_SPRITE = {
  ArrowDown:  0,
  ArrowLeft:  1,
  ArrowRight: 2,
  ArrowUp:    3,
};

// Dynamically load all sprites from the bulba folder (mirrors PokeRender pattern)
const spriteCtx = require.context('../images/bulba/Normal', false);
const ALL_SPRITES = spriteCtx.keys()
  .filter(k => /\/\d{3}\.png$/.test(k))   // only 3-digit base forms, no variants
  .sort()
  .map(key => {
    const mod = spriteCtx(key);
    const img = mod && typeof mod === 'object' && mod.default ? mod.default : mod;
    return { id: key.replace('./', '').replace('.png', ''), img };
  });

const shinyCtx = require.context('../images/bulba/Shiny', false);
const ALL_SHINY_SPRITES = shinyCtx.keys()
  .filter(k => /\/\d{3}s\.png$/.test(k))  // only 3-digit base shiny forms
  .sort()
  .map(key => {
    const mod = shinyCtx(key);
    const img = mod && typeof mod === 'object' && mod.default ? mod.default : mod;
    return { id: key.replace('./', '').replace('s.png', ''), img };
  });

const DEFAULT_IMG = redSprite;

// Dex numbers whose overworld sprite sheets use larger-than-standard frames
const EXTRA_LARGE_DEX = new Set(['208', '249', '250', '382', '383', '384', '486', '487']);

// Per-direction 4-frame walk cycle for Red. Coordinates are [col, row] matching
// the user-provided layout. The inbetween frame is repeated at positions 1 and 3.
// dirNum order matches DIR_TO_SPRITE: 0=Down, 1=Left, 2=Right, 3=Up
const RED_DIR_FRAMES = [
  [[2,2],[2,1],[2,3],[2,1]], // Down:  step1 → inbetween → step2 → inbetween
  [[0,1],[0,2],[0,3],[0,2]], // Left:  step1 → inbetween → step2 → inbetween
  [[1,1],[1,0],[1,2],[1,0]], // Right: step1 → inbetween → step2 → inbetween
  [[2,0],[0,0],[1,3],[0,0]], // Up:    step1 → inbetween → step2 → inbetween
];

function useWindowWidth() {
  const [width, setWidth] = useState(window.innerWidth);
  useEffect(() => {
    const onResize = () => setWidth(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return width;
}

function useAnimFrame() {
  const [count, setCount] = useState(0);
  const requestRef = useRef();
  const prevTimeRef = useRef();

  useEffect(() => {
    const animate = time => {
      if (prevTimeRef.current !== undefined) {
        const delta = time - prevTimeRef.current;
        setCount(prev => (prev + delta * 0.0035) % 4);
      }
      prevTimeRef.current = time;
      requestRef.current = requestAnimationFrame(animate);
    };
    requestRef.current = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(requestRef.current);
  }, []);

  return Math.floor(count);
}

export default function Game({ dark }) {
  const windowWidth = useWindowWidth();
  const isMobile = windowWidth < 768;
  const ROWS = isMobile ? ROWS_MOBILE : ROWS_DESKTOP;
  const CELL_SIZE = isMobile ? 78 : 104;
  const SPRITE_MULT = isMobile ? 1.5 : 2;
  const COLS = Math.max(3, Math.floor((windowWidth - 200) / CELL_SIZE));

  const [body, setBody] = useState([
    { row: 3, col: 2, direction: 'ArrowRight', sprite: DEFAULT_IMG },
  ]);
  const [showPicker, setShowPicker] = useState(false);
  const [hoveredId, setHoveredId] = useState(null);
  const [filter, setFilter] = useState('');
  const [shiny, setShiny] = useState(false);
  const [tileMode, setTileMode] = useState('new');
  const pickerScrollRef = useRef();
  const savedScrollPos = useRef(0);

  const colsRef = useRef(COLS);
  useEffect(() => { colsRef.current = COLS; }, [COLS]);

  // Background music
  const audioRef = useRef(null);
  const [muted, setMuted] = useState(false);
  const [trackIndex, setTrackIndex] = useState(0);

  useEffect(() => {
    const audio = new Audio(TRACKS[trackIndex].src);
    audio.loop = true;
    audio.volume = 0.5;
    if (!muted) audio.play().catch(() => {});
    audioRef.current = audio;
    return () => {
      audio.pause();
      audio.currentTime = 0;
    };
  }, [trackIndex]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleMute = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (muted) {
      audio.play().catch(() => {});
    } else {
      audio.pause();
    }
    setMuted(m => !m);
  };

  const prevTrack = () => setTrackIndex(i => (i - 1 + TRACKS.length) % TRACKS.length);
  const nextTrack = () => setTrackIndex(i => (i + 1) % TRACKS.length);

  const frame = useAnimFrame();

  const MOVES = {
    ArrowUp:    { row: -1, col:  0 },
    ArrowDown:  { row:  1, col:  0 },
    ArrowLeft:  { row:  0, col: -1 },
    ArrowRight: { row:  0, col:  1 },
  };

  const move = useCallback((key) => {
    const delta = MOVES[key];
    if (!delta) return;
    setBody(prev => {
      const newHead = {
        ...prev[0],
        row: Math.max(0, Math.min(ROWS - 1, prev[0].row + delta.row)),
        col: Math.max(0, Math.min(colsRef.current - 1, prev[0].col + delta.col)),
        direction: key,
      };
      const newBody = [newHead];
      for (let i = 1; i < prev.length; i++) {
        newBody.push({ ...prev[i - 1], sprite: prev[i].sprite });
      }
      return newBody;
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleKey = useCallback((e) => {
    if (!MOVES[e.key]) return;
    e.preventDefault();
    move(e.key);
  }, [move]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [handleKey]);

  useEffect(() => {
    setBody(prev => prev.map(seg => ({
      ...seg,
      col: Math.min(seg.col, COLS - 1),
    })));
  }, [COLS]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (showPicker && pickerScrollRef.current) {
      pickerScrollRef.current.scrollTop = savedScrollPos.current;
    }
  }, [showPicker]);

  const addSegment = (sprite) =>
    setBody(prev => [...prev, { ...prev[prev.length - 1], sprite }]);

  const removeSegment = () =>
    setBody(prev => prev.length > 1 ? prev.slice(0, -1) : prev);

  const clearSegments = () =>
    setBody(prev => [prev[0]]);

  const addRandomParty = () => {
    const usedSprites = new Set(body.map(seg => seg.sprite));
    const normalPool = ALL_SPRITES.filter(s => !EXTRA_LARGE_DEX.has(s.id) && !usedSprites.has(s.img));
    const shinyPool  = ALL_SHINY_SPRITES.filter(s => !EXTRA_LARGE_DEX.has(s.id) && !usedSprites.has(s.img));
    setBody(prev => {
      const newSegs = Array.from({ length: 20 }, () => {
        const pickShiny = shinyPool.length > 0 && Math.random() < 0.05;
        const pool = pickShiny ? shinyPool : normalPool;
        if (!pool.length) return null;
        return { ...prev[prev.length - 1], sprite: pool[Math.floor(Math.random() * pool.length)].img };
      }).filter(Boolean);
      return [...prev, ...newSegs];
    });
  };

  const cellMap = new Map();
  body.forEach((seg, i) => {
    const key = `${seg.row},${seg.col}`;
    if (!cellMap.has(key)) cellMap.set(key, i);
  });

  const borderColor = dark ? '#555' : '#aaa';
  const bgColor     = dark ? '#1e1e1e' : '#f5f5f5';
  const cellBg      = dark ? '#2a2a2a' : '#fff';
  const textColor   = dark ? '#ccc' : '#444';

  const renderSegment = (f, seg, dirNum) => {
    const rf = 3 - f;
    if (seg.sprite === redSprite) {
      const [col, row] = RED_DIR_FRAMES[dirNum][rf];
      return SpriteFrame(redSprite, row, col, SPRITE_MULT, 3, 4);
    }
    return SpriteFourStep(rf, seg.sprite, 0, dirNum, SPRITE_MULT);
  };

  const trimmed = filter.trim();
  const spritePool = shiny ? ALL_SHINY_SPRITES : ALL_SPRITES;
  const filtered = trimmed
    ? spritePool.filter(s => s.id.includes(trimmed))
    : spritePool;

  return (
    <div style={{ padding: '2rem', textAlign: 'center' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.6rem', marginBottom: '0.25rem' }}>
        <h2 style={{ margin: 0 }}>Poké Playground</h2>
        <button onClick={toggleMute} title={muted ? 'Unmute' : 'Mute'}
          style={{ fontSize: '1.1rem', background: 'none', border: 'none', cursor: 'pointer', padding: '2px 4px', lineHeight: 1, opacity: muted ? 0.4 : 1 }}>
          {muted ? '🔇' : '🎵'}
        </button>
        <button onClick={prevTrack} title="Previous track"
          style={{ fontSize: '0.85rem', background: 'none', border: 'none', cursor: 'pointer', padding: '2px 4px', color: dark ? '#ccc' : '#555' }}>
          ◀
        </button>
        <span style={{ fontSize: '0.78rem', color: dark ? '#bbb' : '#666', minWidth: 90, textAlign: 'center' }}>
          {TRACKS[trackIndex].label}
        </span>
        <button onClick={nextTrack} title="Next track"
          style={{ fontSize: '0.85rem', background: 'none', border: 'none', cursor: 'pointer', padding: '2px 4px', color: dark ? '#ccc' : '#555' }}>
          ▶
        </button>
      </div>
      <h4 style={{ color: dark ? '#fffdfd' : '#555', marginBottom: '1rem'}}>
        Use arrow keys or the D-pad to move. Add or remove segments below.
      </h4>

      <div style={{ marginBottom: '1.5rem', display: 'flex', justifyContent: 'center', gap: '1rem', alignItems: 'center' }}>
        <button
          onClick={removeSegment}
          disabled={body.length <= 1}
          style={{
            width: 40, height: 40, fontSize: '1.4rem', fontWeight: 'bold',
            borderRadius: '50%', border: `2px solid ${borderColor}`,
            background: cellBg, color: dark ? '#fff' : '#333',
            cursor: body.length <= 1 ? 'not-allowed' : 'pointer',
            opacity: body.length <= 1 ? 0.4 : 1,
          }}
        >
          −
        </button>

        <span style={{ color: '#fffdfd', minWidth: 80, fontSize: "2rem" }}>
          {body.length} segment{body.length !== 1 ? 's' : ''}
        </span>

        <button
          onClick={() => { setShowPicker(true); setFilter(''); }}
          style={{
            width: 40, height: 40, fontSize: '1.4rem', fontWeight: 'bold',
            borderRadius: '50%', border: `2px solid ${borderColor}`,
            background: cellBg, color: dark ? '#fff' : '#333',
            cursor: 'pointer',
          }}
        >
          +
        </button>

      </div>

      <div style={{ marginBottom: '1.5rem', display: 'flex', justifyContent: 'center', gap: '0.75rem' }}>
        <button
          onClick={addRandomParty}
          style={{
            padding: '0 14px', height: 40, fontSize: '0.85rem', fontWeight: 'bold',
            borderRadius: 20, border: `2px solid ${borderColor}`,
            background: cellBg, color: dark ? '#fff' : '#333',
            cursor: 'pointer', whiteSpace: 'nowrap',
          }}
        >
          🎲 Random ×20
        </button>
        <button
          onClick={clearSegments}
          disabled={body.length <= 1}
          style={{
            padding: '0 14px', height: 40, fontSize: '0.85rem', fontWeight: 'bold',
            borderRadius: 20, border: `2px solid ${borderColor}`,
            background: cellBg, color: dark ? '#fff' : '#333',
            cursor: body.length <= 1 ? 'not-allowed' : 'pointer',
            opacity: body.length <= 1 ? 0.4 : 1,
            whiteSpace: 'nowrap',
          }}
        >
          🗑 Clear
        </button>
      </div>

      {/* Sprite picker modal */}
      {showPicker && (
        <div
          onClick={() => setShowPicker(false)}
          style={{
            position: 'fixed', inset: 0,
            background: 'rgba(0,0,0,0.5)',
            zIndex: 200,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: cellBg,
              border: `2px solid ${borderColor}`,
              borderRadius: 10,
              padding: '1.25rem',
              width: 420,
              maxWidth: '92vw',
              display: 'flex',
              flexDirection: 'column',
              maxHeight: '80vh',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem', flexShrink: 0 }}>
              <h3 style={{ color: textColor, margin: 0 }}>
                Choose a Pokémon
              </h3>
              <button
                onClick={() => setShiny(s => !s)}
                style={{
                  padding: '4px 10px',
                  borderRadius: 6,
                  border: `2px solid ${shiny ? '#f5c518' : borderColor}`,
                  background: shiny ? (dark ? '#3a3000' : '#fffbe6') : 'transparent',
                  color: shiny ? '#f5c518' : textColor,
                  cursor: 'pointer',
                  fontSize: '0.8rem',
                  fontWeight: 'bold',
                  transition: 'all 0.15s',
                }}
              >
                ★ Shiny
              </button>
            </div>

            <input
              autoFocus
              placeholder="Filter by dex # (e.g. 025)"
              value={filter}
              onChange={e => setFilter(e.target.value)}
              style={{
                marginBottom: '0.75rem',
                padding: '6px 10px',
                borderRadius: 6,
                border: `1px solid ${borderColor}`,
                background: dark ? '#1a1a1a' : '#fff',
                color: textColor,
                fontSize: '1rem',
                flexShrink: 0,
                outline: 'none',
              }}
            />

            <div ref={pickerScrollRef} style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(72px, 1fr))',
              gap: 4,
              overflowY: 'auto',
              overflowX: 'hidden',
              paddingRight: 4,
            }}>
              {filtered.map(s => (
                <div
                  key={s.id}
                  title={`#${s.id}`}
                  onMouseEnter={() => setHoveredId(s.id)}
                  onMouseLeave={() => setHoveredId(null)}
                  onClick={() => { savedScrollPos.current = pickerScrollRef.current?.scrollTop ?? 0; addSegment(s.img); setShowPicker(false); setHoveredId(null); }}
                  style={{
                    cursor: 'pointer',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    padding: '4px 2px',
                    borderRadius: 6,
                    border: `2px solid ${hoveredId === s.id ? (dark ? '#88aaff' : '#4466cc') : 'transparent'}`,
                    background: hoveredId === s.id ? (dark ? '#333' : '#eef') : 'transparent',
                    transition: 'border-color 0.1s, background 0.1s',
                  }}
                >
                  <div style={{ transform: 'translateY(-6px)', pointerEvents: 'none' }}>
                    {SpriteFourStep(3 - frame, s.img, 0, 0, PICKER_MULT)}
                  </div>
                  <span style={{
                    fontSize: '0.55rem',
                    color: textColor,
                    textAlign: 'center',
                    marginTop: -4,
                    lineHeight: 1.2,
                  }}>
                    #{s.id}
                  </span>
                </div>
              ))}
              {filtered.length === 0 && (
                <span style={{ color: textColor, gridColumn: '1/-1', padding: '1rem', fontSize: '0.85rem' }}>
                  No sprites match "{filter}"
                </span>
              )}
            </div>
          </div>
        </div>
      )}

      <div style={{ marginBottom: '0.5rem' }}>
        <button
          onClick={() => setTileMode(m => m === 'new' ? 'original' : 'new')}
          style={{
            fontSize: '0.75rem', padding: '2px 10px',
            border: `1px solid ${borderColor}`, borderRadius: 4,
            background: cellBg, color: textColor, cursor: 'pointer',
          }}
        >
          tiles: {tileMode === 'new' ? 'LightGrass' : 'original'}
        </button>
      </div>

      <div style={{ display: 'inline-block', border: `2px solid ${borderColor}`, background: bgColor, borderRadius: '6px', padding: '3px', overflow: 'hidden' }}>
        {Array.from({ length: ROWS }, (_, r) => (
          <div key={r} style={{ display: 'flex' }}>
            {Array.from({ length: COLS }, (_, c) => {
              const segIndex = cellMap.get(`${r},${c}`);
              const hasSeg = segIndex !== undefined;
              const seg = hasSeg ? body[segIndex] : null;
              const dirNum = hasSeg ? (DIR_TO_SPRITE[seg.direction] ?? 2) : 2;
              const isHead = segIndex === 0;
              const isTopEdge    = r === 0;
              const isBottomEdge = r === ROWS - 1;
              const isLeftEdge   = c === 0;
              const isRightEdge  = c === COLS - 1;
              const tileSet = tileMode === 'new'
                ? { tl: dark ? dgTopLeft : lgTopLeft, tc: dark ? dgTop : lgTop, tr: dark ? dgTopRight : lgTopRight,
                    ml: dark ? dgLeft : lgLeft,        mc: dark ? dgCenter : lgCenter, mr: dark ? dgRight : lgRight,
                    bl: dark ? dgBottomLeft : lgBottomLeft, bc: dark ? dgBottom : lgBottom, br: dark ? dgBottomRight : lgBottomRight }
                : tileMode === 'original'
                ? { tl: ogTopLeft, tc: ogTop, tr: ogTopRight, ml: ogLeft, mc: ogCenter, mr: ogRight, bl: ogBottomLeft, bc: ogBottom, br: ogBottomRight }
                : null;

              let tileBg = grassFlat;
              if (tileSet) {
                tileBg = tileSet.mc;
                if      (isTopEdge    && isLeftEdge)                   tileBg = tileSet.tl;
                else if (isTopEdge    && isRightEdge)                  tileBg = tileSet.tr;
                else if (isBottomEdge && isLeftEdge)                   tileBg = tileSet.bl;
                else if (isBottomEdge && isRightEdge)                  tileBg = tileSet.br;
                else if (isTopEdge    && !isLeftEdge && !isRightEdge)  tileBg = tileSet.tc;
                else if (isBottomEdge && !isLeftEdge && !isRightEdge)  tileBg = tileSet.bc;
                else if (isLeftEdge   && !isTopEdge  && !isBottomEdge) tileBg = tileSet.ml;
                else if (isRightEdge  && !isTopEdge  && !isBottomEdge) tileBg = tileSet.mr;
              }
              return (
                <div
                  key={c}
                  style={{
                    width: CELL_SIZE,
                    height: CELL_SIZE,
                    // border: `1px solid ${borderColor}`,
                    backgroundImage: `url(${tileBg})`,
                    backgroundSize: 'cover',
                    display: 'flex',
                    alignItems: 'flex-end',
                    justifyContent: 'center',
                    opacity: hasSeg && !isHead ? 1 : 1,
                  }}
                >
                  <div style={{ transform: 'translateY(8px)' }}>
                    {hasSeg && renderSegment(frame, seg, dirNum)}
                  </div>
                </div>
              );
            })}
          </div>
        ))}
      </div>

      {/* D-pad */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: `repeat(3, ${CELL_SIZE / 2}px)`,
        gridTemplateRows: `repeat(3, ${CELL_SIZE / 2}px)`,
        gap: 4,
        marginTop: '1.5rem',
        justifyContent: 'center',
      }}>
        {[
          { key: null },
          { key: 'ArrowUp',    label: '▲' },
          { key: null },
          { key: 'ArrowLeft',  label: '◀' },
          { key: null },
          { key: 'ArrowRight', label: '▶' },
          { key: null },
          { key: 'ArrowDown',  label: '▼' },
          { key: null },
        ].map((btn, i) => (
          btn.key ? (
            <button
              key={i}
              onPointerDown={e => { e.preventDefault(); move(btn.key); }}
              style={{
                width: CELL_SIZE / 2, height: CELL_SIZE / 2,
                fontSize: '1.2rem',
                border: `2px solid ${borderColor}`,
                borderRadius: 8,
                background: cellBg,
                color: dark ? '#fff' : '#333',
                cursor: 'pointer',
                userSelect: 'none',
                touchAction: 'none',
              }}
            >
              {btn.label}
            </button>
          ) : (
            <div key={i} />
          )
        ))}
      </div>

    </div>
  );
}
